import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";
import { apiGet, apiPost, apiPut } from "@/lib/api-client";
export interface UserGamification {
    id: string;
    user_id: string;
    xp: number;
    level: number;
    current_streak: number;
    longest_streak: number;
    last_activity_date: string | null;
    streak_freezes_available: number;
    streak_freezes_used: number;
    created_at: string;
    updated_at: string;
}

export interface AwardXpResult {
    new_xp: number;
    new_level: number;
    level_up: boolean;
    current_streak: number;
    streak_updated: boolean;
}

export interface UpdateStreakResult {
    current_streak: number;
    longest_streak: number;
    streak_continued: boolean;
    new_milestones: Array<{
        milestone_id: string;
        streak_days: number;
        badge_name: string;
        xp_bonus: number;
        freezes_awarded: number;
        is_new: boolean;
    }> | null;
    freeze_used: boolean;
    freezes_remaining: number;
}

export interface StreakCalendarDay {
    activity_date: string;
    xp_earned: number;
    challenges_completed: number;
    practice_sessions: number;
    freeze_used: boolean;
}

export interface StreakMilestone {
    id: string;
    streak_days: number;
    xp_bonus: number;
    freezes_awarded: number;
    badge_name: string;
}

export interface UserStreakMilestone {
    id: string;
    user_id: string;
    milestone_id: string;
    achieved_at: string;
    is_claimed: boolean;
    streak_milestones?: StreakMilestone;
}

export interface UseStreakFreezeResult {
    success: boolean;
    current_streak: number;
    freezes_remaining: number;
    message: string;
}

export interface ClaimMilestoneResult {
    success: boolean;
    xp_awarded: number;
    freezes_awarded: number;
    badge_name: string;
}

export const calculateLevel = (xp: number): number => {
    return Math.floor(Math.sqrt(xp / 100)) + 1;
};

export const xpForNextLevel = (level: number): number => {
    return level * level * 100;
};

export const levelProgress = (xp: number): number => {
    const currentLevel = calculateLevel(xp);
    const currentLevelXp = (currentLevel - 1) * (currentLevel - 1) * 100;
    const nextLevelXp = currentLevel * currentLevel * 100;
    const progressXp = xp - currentLevelXp;
    const neededXp = nextLevelXp - currentLevelXp;
    return Math.min(100, (progressXp / neededXp) * 100);
};

export const getStreakMotivation = (streak: number): string => {
    // `<= 0` rather than `=== 0`: a streak can never be negative in the
    // database, but a negative number should read as "no streak yet" rather
    // than falling through to the "great start" band.
    if (streak <= 0) return "Start your streak today!";
    if (streak <= 2) return "Great start! Keep going!";
    if (streak <= 6) return "You're on fire!";
    if (streak <= 13) return "Incredible consistency!";
    if (streak <= 29) return "Unstoppable!";
    return "Legendary dedication!";
};

export const getNextMilestoneInfo = (
    currentStreak: number,
    milestones: StreakMilestone[]
): { daysToNext: number; badgeName: string } | null => {
    const sorted = [...milestones].sort((a, b) => a.streak_days - b.streak_days);
    const next = sorted.find((m) => m.streak_days > currentStreak);
    if (!next) return null;
    return { daysToNext: next.streak_days - currentStreak, badgeName: next.badge_name };
};

export const useGamification = () => {
    const { userId } = useAuth();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: queryKeys.gamification.byUser(userId),
        enabled: !!userId,
        queryFn: async (): Promise<UserGamification | null> => {
            if (!userId) return null;

            const { data, error } = await (db as any).from("user_gamification")
                .select("*")
                .eq("user_id", userId)
                .single();

            if (error && error.code === "PGRST116") {
                // No gamification row yet. Clients cannot insert one (the table is
                // read-only to authenticated users); award_xp creates it on the
                // first XP event, so report an empty profile until then.
                return null;
            }

            if (error) {
                console.error("Error fetching gamification:", error);
                return null;
            }

            return data as UserGamification;
        },
        staleTime: 1000 * 60 * 5,
    });

    const addXpMutation = useMutation({
        mutationFn: async (xpAmount: number): Promise<AwardXpResult | null> => {
            if (!userId) return null;

            // The learner comes from the token; only the amount is sent.
            const data = await apiPost<unknown[]>("/v1/gamification/xp", { amount: xpAmount });
            const error = null;

            if (error) {
                console.error("Error awarding XP:", error);
                throw error;
            }

            return data?.[0] as AwardXpResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
        },
    });

    const updateStreakMutation = useMutation({
        mutationFn: async (): Promise<UpdateStreakResult | null> => {
            if (!userId) return null;

            const data = await apiPut<{ updated: unknown[] }>("/v1/gamification/streak", {}).then(
                (r) => r.updated,
            );
            const error = null;

            if (error) {
                console.error("Error updating streak:", error);
                throw error;
            }

            return data?.[0] as UpdateStreakResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.streakCalendar.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.streakMilestones.byUser(userId) });
        },
    });

    return useMemo(() => ({
        data: query.data,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
        addXp: addXpMutation.mutateAsync,
        isAddingXp: addXpMutation.isPending,
        updateStreak: updateStreakMutation.mutateAsync,
        isUpdatingStreak: updateStreakMutation.isPending,
        level: query.data?.level ?? 1,
        xp: query.data?.xp ?? 0,
        currentStreak: query.data?.current_streak ?? 0,
        longestStreak: query.data?.longest_streak ?? 0,
        freezesAvailable: query.data?.streak_freezes_available ?? 0,
        freezesUsed: query.data?.streak_freezes_used ?? 0,
        levelProgress: levelProgress(query.data?.xp ?? 0),
        xpToNextLevel: xpForNextLevel(query.data?.level ?? 1),
    }), [query.data, query.isLoading, query.error, query.refetch, addXpMutation.mutateAsync, addXpMutation.isPending, updateStreakMutation.mutateAsync, updateStreakMutation.isPending]);
};

export const useStreakCalendar = (days: number = 30) => {
    const { userId } = useAuth();

    return useQuery({
        queryKey: queryKeys.streakCalendar.byUser(userId, days),
        enabled: !!userId,
        queryFn: async (): Promise<StreakCalendarDay[]> => {
            if (!userId) return [];

            const data = await apiGet<unknown[]>(`/v1/gamification/streak?days=${days}`);
            const error = null;

            if (error) {
                console.error("Error fetching streak calendar:", error);
                return [];
            }

            return (data || []) as StreakCalendarDay[];
        },
        staleTime: 1000 * 60 * 5,
     });
};

export interface UseStreakMilestonesOptions {
    /**
     * Gate both milestone queries.
     *
     * `StreakDetailModal` is mounted at the app root (its `open` prop only hides
     * it), so without this flag every page fetched the milestone catalogue and
     * the learner's earned badges for a dialog nobody had opened.
     */
    enabled?: boolean;
}

export const useStreakMilestones = ({ enabled = true }: UseStreakMilestonesOptions = {}) => {
    const { userId } = useAuth();
    const queryClient = useQueryClient();

    const allMilestones = useQuery({
        queryKey: queryKeys.streakMilestones.all(),
        enabled,
        queryFn: async (): Promise<StreakMilestone[]> => {
            const { data, error } = await (db as any).from("streak_milestones")
                .select("*")
                .order("streak_days", { ascending: true });

            if (error) {
                console.error("Error fetching milestones:", error);
                return [];
            }

            return (data || []) as StreakMilestone[];
        },
        staleTime: 1000 * 60 * 60,
    });

    const userMilestones = useQuery({
        queryKey: queryKeys.streakMilestones.byUser(userId),
        enabled: enabled && !!userId,
        queryFn: async (): Promise<UserStreakMilestone[]> => {
            if (!userId) return [];

            // The table gateway is a column gateway, not PostgREST: it does not
            // expand embedded relations, so `streak_milestones(*)` came back as a
            // 400 ("Unsupported column expression") and this list was always
            // empty. Two plain reads and a join here instead.
            const { data, error } = await (db as any).from("user_streak_milestones")
                .select("*")
                .eq("user_id", userId)
                .order("achieved_at", { ascending: true });

            if (error) {
                console.error("Error fetching user milestones:", error);
                return [];
            }

            const catalogue: StreakMilestone[] = allMilestones.data ?? [];
            const byId = new Map(catalogue.map((milestone) => [milestone.id, milestone]));

            return ((data || []) as Array<UserStreakMilestone & { milestone_id: string }>).map((row) => ({
                ...row,
                streak_milestones: byId.get(row.milestone_id) ?? null,
            })) as UserStreakMilestone[];
        },
        staleTime: 1000 * 60 * 5,
    });

    const claimMilestoneMutation = useMutation({
        mutationFn: async (milestoneId: string): Promise<ClaimMilestoneResult | null> => {
            if (!userId) return null;

            const claimed = await apiPost<ClaimMilestoneResult>(
                `/v1/gamification/milestones/${encodeURIComponent(milestoneId)}/claims`,
                {}
            );
            return claimed as ClaimMilestoneResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.streakMilestones.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
        },
    });

    return useMemo(() => ({
        allMilestones: allMilestones.data ?? [],
        userMilestones: userMilestones.data ?? [],
        isLoading: allMilestones.isLoading || userMilestones.isLoading,
        claimMilestone: claimMilestoneMutation.mutateAsync,
        isClaiming: claimMilestoneMutation.isPending,
    }), [allMilestones.data, allMilestones.isLoading, userMilestones.data, userMilestones.isLoading, claimMilestoneMutation.mutateAsync, claimMilestoneMutation.isPending]);
};

export const useStreakFreeze = () => {
    const { userId } = useAuth();
    const queryClient = useQueryClient();

    const useFreezeMutation = useMutation({
        mutationFn: async (): Promise<UseStreakFreezeResult | null> => {
            if (!userId) return null;

            const { result } = await apiPost<{ result: unknown[] }>("/v1/gamification/streak/freezes", {});
            return (result?.[0] ?? null) as UseStreakFreezeResult | null;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
        },
    });

     return useMemo(() => ({
         useFreeze: useFreezeMutation.mutateAsync,
         isUsingFreeze: useFreezeMutation.isPending,
     }), [useFreezeMutation.mutateAsync, useFreezeMutation.isPending]);
 };

export const useWeeklyStreak = () => {
    const { userId } = useAuth();
    const { data: calendar, isLoading } = useStreakCalendar(7);
    const { userMilestones } = useStreakMilestones();

    const weeklyStreak = useMemo(() => {
        if (!calendar || calendar.length === 0) return 0;
        const today = new Date();
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);
        const mondayStr = monday.toISOString().split('T')[0];
        return calendar.filter((day) => day.activity_date >= mondayStr && day.practice_sessions > 0).length;
    }, [calendar]);

    const weeklyCalendar = useMemo(() => {
        const cal = calendar ?? [];

        const today = new Date();
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);

        // Build full week: Mon, Tue, Wed, Thu, Fri, Sat, Sun
        const fullWeek = [];
        for (let i = 0; i < 7; i++) {
            const currentDate = new Date(monday);
            currentDate.setDate(monday.getDate() + i);
            const dateStr = currentDate.toISOString().split('T')[0];

            // Try to find existing data for this date
            const existing = cal.find(d => d.activity_date === dateStr);

            fullWeek.push({
                activity_date: dateStr,
                practice_sessions: existing?.practice_sessions || 0,
                xp_earned: existing?.xp_earned || 0,
                challenges_completed: existing?.challenges_completed || 0,
                freeze_used: existing?.freeze_used || false,
            });
        }

        return fullWeek;
    }, [calendar]);

    // Enrich weeklyCalendar with milestone info
    const enrichedWeeklyCalendar = useMemo(() => {
        if (!weeklyCalendar.length) return [];

        return weeklyCalendar.map((day) => {
            // Check if any milestone was achieved on this day
            const milestoneAchieved = userMilestones?.find(
                (um) => um.achieved_at && new Date(um.achieved_at).toISOString().split('T')[0] === day.activity_date
            );

            return {
                ...day,
                isMilestone: !!milestoneAchieved,
                milestoneInfo: milestoneAchieved?.streak_milestones || null,
            };
        });
    }, [weeklyCalendar, userMilestones]);

    return { weeklyStreak, isLoading, weeklyCalendar: enrichedWeeklyCalendar };
};
